import { Component, inject, OnInit } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import {
    FormsModule,
    ReactiveFormsModule,
    FormBuilder,
    FormGroup,
    Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CustomizerSettingsService } from '../../customizer-settings/customizer-settings.service';
import { TranslocoModule } from '@ngneat/transloco';
import { ApiService } from '../../services/api/api.service';
import { Company } from '../../interfaces/Company';
import { CompanyResponse } from '../../interfaces/CompanyResponse';
import { BranchOffice } from '../../interfaces/BranchOffice';
import { BranchOfficeResponse } from '../../interfaces/BranchOfficeResponse';
import { MatDialog } from '@angular/material/dialog';
import { AlertDialogComponent } from '../../common/alert-dialog/alert-dialog.component';

@Component({
    selector: 'app-m-edit-branch-office',
    standalone: true,
    imports: [
        MatCardModule,
        MatMenuModule,
        MatButtonModule,
        RouterLink,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatDatepickerModule,
        MatNativeDateModule,
        ReactiveFormsModule,
        TranslocoModule,
        NgFor,
        NgIf,
    ],
    templateUrl: './m-edit-branch-office.component.html',
    styleUrl: './m-edit-branch-office.component.scss',
})
export class MEditBranchOfficeComponent implements OnInit {
    // isToggled
    isToggled = false;

    // Form
    editBranchOfficeForm: FormGroup;

    // Show alert
    showAlert: boolean = false;

    // Array of companies
    companies: Company[] = [];

    // Id
    id: string;

    // Branch office
    editBranchOffice: BranchOffice | undefined;

    readonly dialog = inject(MatDialog);

    constructor(
        private fb: FormBuilder,
        public themeService: CustomizerSettingsService,
        private apiService: ApiService,
        private router: Router,
        private route: ActivatedRoute
    ) {
        this.editBranchOfficeForm = this.fb.group({
            name: ['', [Validators.required, Validators.maxLength(200)]],
            address: ['', [Validators.required, Validators.maxLength(300)]],
        });
        this.themeService.isToggled$.subscribe((isToggled) => {
            this.isToggled = isToggled;
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    ngOnInit() {
        this.apiService
            .getCompanyPagination(0, 1000000, 'name', 'asc')
            .subscribe((response: CompanyResponse) => {
                this.companies.push(...response.companies);
            });

        this.route.params.subscribe((params) => {
            this.id = params['id'];

            this.apiService
                .getBranchOfficeById(this.id)
                .subscribe((data: BranchOfficeResponse) => {
                    this.editBranchOffice = data.branchOffice;
                });
        });
    }

    // RTL Mode
    toggleRTLEnabledTheme() {
        this.themeService.toggleRTLEnabledTheme();
    }

    /**
     * On submit
     */
    onSubmit(): void {
        // Return if the form is invalid
        if (this.editBranchOfficeForm.invalid) {
            this.showAlert = false;
            return;
        }

        if (this.editBranchOffice) {
            // Disable the form
            this.editBranchOfficeForm.disable();

            // Hide the alert
            this.showAlert = false;

            // Edit Branch office
            this.apiService
                .updateBranchOffice(this.id, this.editBranchOffice)
                .subscribe({
                    next: (value) => {
                        console.info(value);
                        // Re-enable the form
                        this.editBranchOfficeForm.enable();

                        // Show the alert
                        this.showAlert = true;
                    },
                    error: (error) => {
                        let errMsg: string = error;
                        const dialogRef = this.dialog.open(
                            AlertDialogComponent,
                            {
                                data: {
                                    title: 'ERROR',
                                    message: errMsg,
                                },
                            }
                        );
                        // Re-enable the form
                        this.editBranchOfficeForm.enable();

                        // Show the alert
                        this.showAlert = true;
                    },
                    complete: () => {
                        this.router.navigateByUrl('/masters/branch-offices');
                    },
                });
        }
    }
}
